import { Button } from '@odekoteam/doppio';
import { ReactNode } from 'react';
import { Box, Flex, Heading, Paragraph, Text } from 'theme-ui';

import BannerThumbnail from '@/components/Collection/BannerThumbnail';
import MarketPicker, {
  PickableMarket,
} from '@/components/Collection/MarketPicker';

export type MarketExceptionRow = {
  id: string;
  index: number;
  name: string;
  stocked: boolean;
  /** Shown when the row carries its own artwork; hidden rows have none. */
  thumbnail?: { imageKey: string; pendingFile?: File | null };
  struck?: boolean;
  actions: { label: string; primary?: boolean; onPress: () => void }[];
};

export const sectionStyle = {
  p: 5,
  border: '1px solid',
  borderColor: 'neutralLight',
  borderRadius: 1,
} as const;

/**
 * One of the editor's two exception lists — markets with their own artwork,
 * or markets hidden outright. Both are the same shape: a heading with a count,
 * a picker that adds a market to the list, and a row per member with the
 * actions that take it back out. Inheriting the default is the absence of
 * both, so it is never a list.
 */
export default function MarketExceptionSection({
  title,
  description,
  emptyText,
  picker,
  rows,
  disabled,
}: {
  title: string;
  description?: ReactNode;
  emptyText: string;
  picker: {
    label: string;
    markets: PickableMarket[];
    onPick: (index: number) => void;
  };
  rows: MarketExceptionRow[];
  disabled?: boolean;
}) {
  return (
    <Box sx={sectionStyle}>
      <Flex
        sx={{
          gap: 3,
          mb: 3,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: '1 1 320px' }}>
          <Heading variant="headingSm">
            {title} ({rows.length})
          </Heading>
          {description && (
            <Paragraph variant="paragraphSm" sx={{ color: 'neutralDark' }}>
              {description}
            </Paragraph>
          )}
        </Box>
        <Box sx={{ minWidth: 220 }}>
          <MarketPicker
            label={picker.label}
            disabled={disabled}
            markets={picker.markets}
            onPick={picker.onPick}
          />
        </Box>
      </Flex>
      {rows.length === 0 ? (
        <Paragraph variant="paragraphSm" sx={{ color: 'neutralDark' }}>
          {emptyText}
        </Paragraph>
      ) : (
        rows.map((row, position) => (
          <Flex
            key={row.id}
            sx={{
              py: 3,
              gap: 4,
              alignItems: 'center',
              borderTop: position === 0 ? undefined : '1px solid',
              borderColor: 'neutralLight',
            }}
          >
            {row.thumbnail && (
              <BannerThumbnail
                alt={`${row.name} banner`}
                imageKey={row.thumbnail.imageKey}
                pendingFile={row.thumbnail.pendingFile}
              />
            )}
            <Box sx={{ flex: 1 }}>
              <Text
                variant="paragraphMd"
                sx={{ textDecoration: row.struck ? 'line-through' : undefined }}
              >
                {row.name}
              </Text>
              {!row.stocked && (
                <Text variant="paragraphSm" sx={{ color: 'neutral', ml: 2 }}>
                  not stocked here
                </Text>
              )}
            </Box>
            {row.actions.map((action) => (
              <Button
                key={action.label}
                variant={action.primary ? 'secondary' : 'ghost'}
                isDisabled={disabled}
                onPress={action.onPress}
              >
                {action.label}
              </Button>
            ))}
          </Flex>
        ))
      )}
    </Box>
  );
}
